// Protected surface: review docs/protected-surfaces/auth-access.md and docs/protected-surfaces/subscription-lifecycle.md before changing protected-route gating behavior.
// src/ProtectedRoute.js

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import { useUser } from './contexts/userContext';
import { isAdminUser } from './utils/adminAccess';

const PROTECTED_ROUTE_DEBUG = String(process.env.REACT_APP_AUTH_FLOW_DEBUG || '').trim() === '1';
const protectedRouteDebug = (...args) => {
  if (PROTECTED_ROUTE_DEBUG) window.console.log(...args);
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  
  // ✅ FIX: Rely on userContext instead of duplicating ensureSession
  const { user, isLoading, bootstrapped } = useUser();

  // 🔒 CRITICAL: Wait for userContext to be fully bootstrapped
  if (!bootstrapped || isLoading) {
    protectedRouteDebug('[ProtectedRoute] Waiting for userContext to bootstrap...', { bootstrapped, isLoading });
    return <LoadingScreen />;
  }

  // ✅ SIMPLIFIED: If no user, redirect to login
  if (!user) {
    protectedRouteDebug('[ProtectedRoute] No user in context, redirecting to login');
    const redirectPath = location.pathname.startsWith('/app')
      ? `${location.pathname}${location.search || ''}${location.hash || ''}`
      : '/app';
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // ✅ SIMPLIFIED: User exists, check subscription access
  protectedRouteDebug('[ProtectedRoute] User authenticated via context, checking subscription access');

  // Admin users should be constrained to admin area only.
  const isAdmin = isAdminUser(user);
  if (isAdmin) {
    if (!location.pathname.startsWith('/app/admin')) {
      protectedRouteDebug('[ProtectedRoute] Admin user attempted non-admin route, redirecting to /app/admin', {
        attemptedPath: location.pathname
      });
      return <Navigate to="/app/admin" replace />;
    }
    return children;
  }
  
  // Optional subscription gating
  if (!user.subscriptionActive && !user.isOnTrial) {
    if (user.hasEverSubscribed) {
      if (location.pathname !== '/reactivate') {
        protectedRouteDebug('[ProtectedRoute] User needs to reactivate subscription');
        return <Navigate to="/reactivate" replace />;
      }
    } else {
      protectedRouteDebug('[ProtectedRoute] User needs to subscribe');
      return <Navigate to="/pricing" replace />;
    }
  } else if (user.isOnTrial) {
    if (user.trialExpired) {
      protectedRouteDebug('[ProtectedRoute] User trial expired');
      return <Navigate to="/trial-expired" replace />;
    }
  } else {
    if (location.pathname === '/reactivate') {
      protectedRouteDebug('[ProtectedRoute] User active, redirecting from reactivate to app');
      return <Navigate to="/app" replace />;
    }
  }
  
  protectedRouteDebug('[ProtectedRoute] User authorized, allowing access');
  return children;
};

export default ProtectedRoute;
